import { useEffect, useState } from 'react';
import { api } from '../api.js';

const MAX_SHOWN = 8;

// Warns the logged-in user about "skeleton" jobs of theirs -- created on the
// placeholder Account by the Onsite app -- that still need the real Account,
// Contact, Amount etc. filling in. Renders nothing at all when there are none.
//
// Deliberately not collapsible/dismissable (it clears itself once the job's
// fixed), and deliberately silent if the lookup fails: it's an extra nudge on
// top of the Zammad ticket the Onsite app already raises, not worth an error
// box on every page load when EspoCRM has a hiccup.
export default function SkeletonJobsBanner() {
  const [jobs, setJobs] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      if (document.hidden) return;
      api
        .get('/api/widgets/skeleton-jobs')
        .then((d) => {
          if (!cancelled) setJobs(d.jobs || []);
        })
        .catch(() => {});
    };
    load();
    // Someone fixes a job in Espo in another tab and comes back to this one --
    // re-check straight away rather than leaving a stale warning up.
    document.addEventListener('visibilitychange', load);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', load);
    };
  }, []);

  if (jobs.length === 0) return null;

  const shown = jobs.slice(0, MAX_SHOWN);
  const extra = jobs.length - shown.length;

  return (
    <div
      role="status"
      className="mb-4 rounded-lg border border-amber bg-amber/20 px-4 py-3 text-sm text-charcoal dark:text-mist"
    >
      <div className="font-bold">
        ⚠ You have {jobs.length} skeleton job{jobs.length === 1 ? '' : 's'} to complete
      </div>
      <p className="mt-0.5 text-xs">
        Add the real Account, Contact, Amount etc. in EspoCRM — this notice clears once a job is on a real Account.
      </p>
      <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
        {shown.map((j) => (
          <li key={j.id}>
            <a href={j.url} target="_blank" rel="noreferrer" className="font-medium underline">
              {j.jobNumber && <span className="font-mono">{j.jobNumber} </span>}
              {j.name}
            </a>
          </li>
        ))}
        {extra > 0 && <li className="text-xs">+{extra} more</li>}
      </ul>
    </div>
  );
}
